import { useState } from 'react';
import { CalendarClock, Loader2, Pencil, Plus, Tags, Ticket, Trash2, Users, UtensilsCrossed, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { StatusPill } from '@/components/ui/status-pill';
import { SelectField } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { form_error_message } from '@/lib/forms';
import { use_create_offering, use_delete_offering, use_offerings, use_update_offering } from '@/features/dining/hooks';
import type { CreateOfferingInput, DiningBookingMode, DiningOffering, DiningPriceUnit } from '@/features/dining/types';
import { MODE_LABEL } from '@/features/dining/types';
import { ImageGalleryEditor } from '@/components/image-gallery-editor';

const MODE_META: Record<DiningBookingMode, { icon: LucideIcon; blurb: string }> = {
  TABLE_RESERVATION: { icon: UtensilsCrossed, blurb: 'Guests reserve a table for a date, time and party size.' },
  ENTRY_PASS: { icon: Ticket, blurb: 'Sell timed entry to a session (e.g. play with the cats 4–5pm).' },
  EVENT: { icon: CalendarClock, blurb: 'A one-off ticketed experience on a fixed date.' },
};

const PRICE_UNIT_OPTIONS: { value: DiningPriceUnit; label: string }[] = [
  { value: 'PER_GUEST', label: 'Per guest' },
  { value: 'FLAT', label: 'Flat fee' },
];

type FormState = {
  mode: DiningBookingMode;
  name: string;
  description: string;
  price_inr: string;
  price_unit: DiningPriceUnit;
  min_party: string;
  max_party: string;
  turn_minutes: string;
  capacity: string;
  event_start: string; // datetime-local
  event_end: string;
  is_active: boolean;
  redeemable_against_bill: boolean;
};

const EMPTY: FormState = {
  mode: 'TABLE_RESERVATION',
  name: '',
  description: '',
  price_inr: '',
  price_unit: 'PER_GUEST',
  min_party: '1',
  max_party: '6',
  turn_minutes: '90',
  capacity: '20',
  event_start: '',
  event_end: '',
  is_active: true,
  redeemable_against_bill: true,
};

/** ISO (UTC) → value for <input type="datetime-local"> in local time. */
function iso_to_local(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const off = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}

function offering_to_form(o: DiningOffering): FormState {
  return {
    mode: o.mode,
    name: o.name,
    description: o.description ?? '',
    price_inr: o.price_inr,
    price_unit: o.price_unit,
    min_party: String(o.min_party),
    max_party: String(o.max_party),
    turn_minutes: o.turn_minutes != null ? String(o.turn_minutes) : '90',
    capacity: String(o.capacity),
    event_start: iso_to_local(o.event_start),
    event_end: iso_to_local(o.event_end),
    is_active: o.is_active,
    redeemable_against_bill: o.redeemable_against_bill,
  };
}

function price_label(o: DiningOffering): string {
  return `₹${o.price_inr}${o.price_unit === 'PER_GUEST' ? ' / guest' : ''}`;
}

function ModeChooser({ value, onChange }: { value: DiningBookingMode; onChange: (m: DiningBookingMode) => void }) {
  return (
    <div className="grid gap-2 sm:grid-cols-3" data-testid="offering-mode-chooser">
      {(Object.keys(MODE_META) as DiningBookingMode[]).map((m) => {
        const { icon: Icon, blurb } = MODE_META[m];
        const on = value === m;
        return (
          <button
            key={m}
            type="button"
            onClick={() => onChange(m)}
            data-testid={`offering-mode-${m}`}
            className={cn(
              'flex flex-col gap-1 rounded-input border-2 p-3 text-left transition-all duration-fast',
              on ? 'border-primary bg-primary/5 shadow-clay-sm' : 'border-input hover:border-primary/40',
            )}
          >
            <span className="flex items-center gap-2 font-semibold">
              <Icon className={cn('h-4 w-4', on ? 'text-primary' : 'text-muted-foreground')} /> {MODE_LABEL[m]}
            </span>
            <span className="text-xs text-muted-foreground">{blurb}</span>
          </button>
        );
      })}
    </div>
  );
}

export function DiningOfferingsPage() {
  const offerings_q = use_offerings();
  const create_m = use_create_offering();
  const update_m = use_update_offering();
  const delete_m = use_delete_offering();

  const offerings = offerings_q.data ?? [];
  const [editing, set_editing] = useState<null | { id?: string }>(null);
  const [form, set_form] = useState<FormState>(EMPTY);
  const [error, set_error] = useState<string | null>(null);

  function open_new() {
    set_error(null);
    set_form(EMPTY);
    set_editing({});
  }
  function open_edit(o: DiningOffering) {
    set_error(null);
    set_form(offering_to_form(o));
    set_editing({ id: o.id });
  }
  function close_form() {
    set_editing(null);
    set_error(null);
  }

  const is_event = form.mode === 'EVENT';
  const saving = create_m.isPending || update_m.isPending;

  function validate(): CreateOfferingInput | string {
    if (!form.name.trim()) return 'Give this offering a name.';
    const price = Number(form.price_inr);
    if (!Number.isFinite(price) || price <= 0) return 'Enter a price greater than 0.';
    const min_party = Number(form.min_party);
    const max_party = Number(form.max_party);
    if (!Number.isInteger(min_party) || min_party < 1) return 'Minimum party size must be at least 1.';
    if (!Number.isInteger(max_party) || max_party < min_party) return 'Maximum party size must be ≥ minimum.';
    const capacity = Number(form.capacity);
    if (!Number.isInteger(capacity) || capacity < 1) return 'Capacity must be at least 1.';

    const base: CreateOfferingInput = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      mode: form.mode,
      price_inr: price,
      price_unit: form.price_unit,
      min_party,
      max_party,
      capacity,
      is_active: form.is_active,
      redeemable_against_bill: form.redeemable_against_bill,
    };

    if (is_event) {
      if (!form.event_start || !form.event_end) return 'Pick the event start and end time.';
      const start = new Date(form.event_start);
      const end = new Date(form.event_end);
      if (start >= end) return 'Event must end after it starts.';
      base.event_start = start.toISOString();
      base.event_end = end.toISOString();
    } else {
      const turn = Number(form.turn_minutes);
      if (!Number.isInteger(turn) || turn < 15) return 'Session length must be at least 15 minutes.';
      base.turn_minutes = turn;
    }
    return base;
  }

  async function submit() {
    set_error(null);
    const result = validate();
    if (typeof result === 'string') return set_error(result);
    try {
      if (editing?.id) {
        // Mode is immutable; send everything else.
        const { mode: _mode, ...rest } = result;
        void _mode;
        await update_m.mutateAsync({ id: editing.id, input: rest });
      } else {
        await create_m.mutateAsync(result);
      }
      close_form();
    } catch (err) {
      set_error(form_error_message(err, 'Could not save this offering.'));
    }
  }

  async function remove(o: DiningOffering) {
    if (!window.confirm(`Delete “${o.name}”? This cannot be undone.`)) return;
    await delete_m.mutateAsync(o.id);
  }

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title="Reservations & passes"
        description="How guests book you — tables, timed entry passes, and ticketed events."
        action={
          !editing && (
            <Button onClick={open_new} variant="gradient" size="sm" data-testid="add-offering">
              <Plus className="h-4 w-4" /> Add offering
            </Button>
          )
        }
      />

      {editing && (
        <Card data-testid="offering-form">
          <div className="flex items-center justify-between border-b-2 border-brand-outline-variant/15 px-5 py-3.5">
            <h2 className="font-heading text-base font-bold">{editing.id ? 'Edit offering' : 'New offering'}</h2>
            <Button variant="ghost" size="icon" onClick={close_form} aria-label="Close" data-testid="offering-cancel">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardContent className="flex flex-col gap-4 p-5">
            {!editing.id && (
              <div className="flex flex-col gap-1.5">
                <Label>Booking type</Label>
                <ModeChooser
                  value={form.mode}
                  // Deduct-from-bill defaults on for table reservations only; the vendor can still change it.
                  onChange={(m) => set_form((f) => ({ ...f, mode: m, redeemable_against_bill: m === 'TABLE_RESERVATION' }))}
                />
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="off-name">Name</Label>
                <Input
                  id="off-name"
                  data-testid="offering-name"
                  value={form.name}
                  onChange={(e) => set_form((f) => ({ ...f, name: e.target.value }))}
                  placeholder={is_event ? 'Puppy yoga morning' : form.mode === 'ENTRY_PASS' ? 'Cat lounge hour' : 'Window table'}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="off-price">Price (₹)</Label>
                <div className="flex gap-2">
                  <Input
                    id="off-price"
                    data-testid="offering-price"
                    type="number"
                    min={1}
                    value={form.price_inr}
                    onChange={(e) => set_form((f) => ({ ...f, price_inr: e.target.value }))}
                    placeholder="300"
                  />
                  <SelectField
                    aria-label="Pricing unit"
                    data-testid="offering-price-unit"
                    value={form.price_unit}
                    onValueChange={(v) => set_form((f) => ({ ...f, price_unit: v as DiningPriceUnit }))}
                    options={PRICE_UNIT_OPTIONS}
                    className="w-36 shrink-0"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="off-min">Min party</Label>
                <Input id="off-min" data-testid="offering-min-party" type="number" min={1} value={form.min_party} onChange={(e) => set_form((f) => ({ ...f, min_party: e.target.value }))} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="off-max">Max party</Label>
                <Input id="off-max" data-testid="offering-max-party" type="number" min={1} value={form.max_party} onChange={(e) => set_form((f) => ({ ...f, max_party: e.target.value }))} />
              </div>

              {is_event ? (
                <>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="off-start">Event start</Label>
                    <Input id="off-start" data-testid="offering-event-start" type="datetime-local" value={form.event_start} onChange={(e) => set_form((f) => ({ ...f, event_start: e.target.value }))} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="off-end">Event end</Label>
                    <Input id="off-end" data-testid="offering-event-end" type="datetime-local" value={form.event_end} onChange={(e) => set_form((f) => ({ ...f, event_end: e.target.value }))} />
                  </div>
                </>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="off-turn" className="flex items-center gap-1">
                    Session length <span className="text-xs font-normal text-muted-foreground">(min)</span>
                  </Label>
                  <Input id="off-turn" data-testid="offering-turn" type="number" min={15} step={15} value={form.turn_minutes} onChange={(e) => set_form((f) => ({ ...f, turn_minutes: e.target.value }))} />
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="off-cap" className="flex items-center gap-1">
                  {is_event ? 'Tickets' : form.mode === 'ENTRY_PASS' ? 'Seats per session' : 'Total covers'}
                  <span className="text-xs font-normal text-muted-foreground">(capacity)</span>
                </Label>
                <Input id="off-cap" data-testid="offering-capacity" type="number" min={1} value={form.capacity} onChange={(e) => set_form((f) => ({ ...f, capacity: e.target.value }))} />
              </div>

              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label htmlFor="off-desc" className="flex items-center gap-1">
                  Description <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                </Label>
                <textarea
                  id="off-desc"
                  data-testid="offering-desc"
                  value={form.description}
                  onChange={(e) => set_form((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="rounded-input border-2 border-input bg-card px-4 py-3 text-base text-foreground shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/15"
                  placeholder="What guests can expect…"
                />
              </div>

              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-input border-2 border-input bg-secondary/40 px-4 py-2.5 sm:col-span-2">
                <span className="flex flex-col">
                  <span className="text-sm font-semibold">Active</span>
                  <span className="text-xs text-muted-foreground">Visible to customers and bookable</span>
                </span>
                <input type="checkbox" data-testid="offering-active" checked={form.is_active} onChange={(e) => set_form((f) => ({ ...f, is_active: e.target.checked }))} className="h-5 w-5 shrink-0 accent-primary" />
              </label>

              {/* Client change (9 Oct 2026): booking amount comes off the bill at the venue. */}
              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-input border-2 border-input bg-secondary/40 px-4 py-2.5 sm:col-span-2">
                <span className="flex flex-col">
                  <span className="text-sm font-semibold">Deduct from the bill</span>
                  <span className="text-xs text-muted-foreground">The booking amount is adjusted against the customer’s bill at your venue</span>
                </span>
                <input type="checkbox" data-testid="offering-redeemable" checked={form.redeemable_against_bill} onChange={(e) => set_form((f) => ({ ...f, redeemable_against_bill: e.target.checked }))} className="h-5 w-5 shrink-0 accent-primary" />
              </label>
            </div>

            {editing.id ? (
              <ImageGalleryEditor owner_type="DINING_OFFERING" owner_id={editing.id} label="Photos" />
            ) : (
              <p className="text-xs text-muted-foreground">Save the offering first to add photos.</p>
            )}

            {error && (
              <p className="text-sm text-destructive" data-testid="offering-form-error" role="alert">
                {error}
              </p>
            )}

            <div className="flex gap-2">
              <Button onClick={submit} variant="gradient" disabled={saving} data-testid="offering-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {editing.id ? 'Save changes' : 'Create offering'}
              </Button>
              <Button onClick={close_form} variant="ghost">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {offerings_q.isLoading ? (
        <Card>
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        </Card>
      ) : offerings_q.error ? (
        <ErrorState data-testid="offerings-error" title="Failed to load offerings" description={form_error_message(offerings_q.error, 'Please try again.')} />
      ) : offerings.length === 0 ? (
        <EmptyState
          data-testid="offerings-empty"
          icon={Tags}
          title="No offerings yet"
          description="Add a table, an entry pass, or an event so customers can book you."
          action={
            <Button onClick={open_new} variant="gradient">
              <Plus className="h-4 w-4" /> Add offering
            </Button>
          }
        />
      ) : (
        <Card>
          <ul className="divide-y-2 divide-brand-outline-variant/10" data-testid="offerings-list">
            {offerings.map((o) => {
              const Icon = MODE_META[o.mode].icon;
              return (
                <li key={o.id} className="flex items-center gap-3 p-4" data-testid={`offering-row-${o.id}`}>
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-button bg-secondary text-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{o.name}</span>
                      <StatusPill tone="neutral">{MODE_LABEL[o.mode]}</StatusPill>
                      {!o.is_active && <StatusPill tone="warning">Inactive</StatusPill>}
                      {o.redeemable_against_bill && <StatusPill tone="success">Deducted from bill</StatusPill>}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {o.min_party}–{o.max_party}</span>
                      <span>{o.mode === 'EVENT' ? `${o.capacity} tickets` : o.mode === 'ENTRY_PASS' ? `${o.capacity} seats/session` : `${o.capacity} covers`}</span>
                      {o.turn_minutes != null && <span>{o.turn_minutes} min</span>}
                    </div>
                  </div>
                  <div className="shrink-0 text-right font-heading text-base font-bold tabular-nums">{price_label(o)}</div>
                  <div className="flex shrink-0 items-center gap-0.5">
                    <Button variant="ghost" size="icon" onClick={() => open_edit(o)} data-testid={`offering-edit-${o.id}`} aria-label="Edit">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(o)} data-testid={`offering-delete-${o.id}`} aria-label="Delete">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}
